// ThemePicker.tsx — wraps PaletteEditor for both light + dark modes,
// applies the result to CSS variables on :root, and persists the choice
// to localStorage. Apps can pass their own brand presets via the
// `extraPresets` prop and a `defaultPresetId` so the picker boots in
// their own visual identity.
//
// Chrome follows DESIGN.md: a single three-slot Card with header / body /
// footer; semantic Button variants with bootstrap icons; Form.Select for
// the preset dropdown; Alert for status feedback.
import { useCallback, useMemo, useState } from 'react';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Dropdown from '../ui/Dropdown';
import Nav from 'react-bootstrap/Nav';
import Alert from 'react-bootstrap/Alert';
import { BsCheck2, BsArrowCounterclockwise, BsSunFill, BsMoonFill } from 'react-icons/bs';
import type { Palette, PaletteSet, LockMap, Role, Mode, SolverContext, Preset } from './solver';
import {
    ROLES,
    PRESETS, makeInitialSet, solveToConvergence,
    SEED_DARK_SEPIA, SEED_LIGHT_SEPIA,
} from './solver';
import type { Vision } from './cvd';
import { VISIONS, VISION_LABELS, VISION_NOTES } from './cvd';
import { oklchToHex, oklchToRgb } from './oklch';
import { PaletteEditor } from './PaletteEditor';

const STORAGE_KEY = 'palette.themeOverride.v1';

export interface ThemePickerProps {
    /** Called after the user clicks Apply with the resulting palette set. */
    onApplied?: (set: PaletteSet) => void;
    /** localStorage key — override only if you need multi-tenant isolation. */
    storageKey?: string;
    /**
     * App-supplied presets, prepended to the built-in list. Lets each SPA
     * ship its own "brand default" starting point (e.g. authz burnt-orange,
     * topograph amber). Order matters — first entry is shown first in the
     * dropdown.
     */
    extraPresets?: Preset[];
    /** Preset id to start on. Falls back to the first available preset. */
    defaultPresetId?: string;
}

interface Stored {
    presetId: string;
    set: PaletteSet;
    /** Colour-vision model the stored set was solved against. */
    vision?: Vision;
}

// CSS variables we override on Apply. Bootstrap surface tokens like
// --bs-card-bg / --bs-border-color are NOT touched — Bootswatch keeps
// owning component chrome — so even after Apply the navbar, cards, modals,
// and forms still look like a coherent Bootstrap app, just re-tinted.
//
// Vars whose name ends in `-rgb` are emitted as a "r, g, b" triplet
// instead of hex, matching Bootstrap's convention for utility classes
// that compose alpha (rgba(var(--bs-primary-rgb), 0.25), etc.).
const cssVarMap: Record<Role, string[]> = {
    bg:          ['--color-bg-base', '--bs-body-bg'],
    surface:     ['--color-bg-card', '--color-bg-elevated', '--color-bg-card-hover'],
    border:      ['--color-border-default', '--color-border-strong', '--color-border-subtle'],
    text:        ['--color-text-primary', '--bs-body-color'],
    textMuted:   ['--color-text-secondary', '--color-text-muted'],
    primary:     ['--color-brand', '--color-brand-strong', '--bs-primary', '--bs-primary-rgb'],
    onPrimary:   ['--color-on-brand', '--color-on-brand-strong', '--color-on-brand-rgb'],
    secondary:   ['--color-accent', '--bs-secondary', '--bs-secondary-rgb'],
    onSecondary: ['--color-on-accent', '--color-on-accent-rgb'],
    link:        ['--color-link', '--bs-link-color'],
    success:     ['--color-success', '--bs-success', '--bs-success-rgb'],
    onSuccess:   ['--color-on-success'],
    warning:     ['--color-warning', '--bs-warning', '--bs-warning-rgb'],
    onWarning:   ['--color-on-warning'],
    danger:      ['--color-danger', '--bs-danger', '--bs-danger-rgb'],
    onDanger:    ['--color-on-danger'],
    info:        ['--color-info', '--bs-info', '--bs-info-rgb'],
    onInfo:      ['--color-on-info'],
    code:        ['--bs-code-color'],
};

function paletteToCss(p: Palette): string {
    const lines: string[] = [];
    for (const role of ROLES) {
        const oklch = p[role];
        const hex = oklchToHex(oklch);
        let triplet: string | null = null;
        for (const v of cssVarMap[role]) {
            if (v.endsWith('-rgb')) {
                if (triplet === null) {
                    const rgb = oklchToRgb(oklch);
                    triplet = `${Math.round(rgb.r * 255)}, ${Math.round(rgb.g * 255)}, ${Math.round(rgb.b * 255)}`;
                }
                lines.push(`    ${v}: ${triplet};`);
            } else {
                lines.push(`    ${v}: ${hex};`);
            }
        }
    }
    return lines.join('\n');
}

export function applyPaletteSet(set: PaletteSet) {
    const styleId = 'palette-overrides';
    let el = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!el) {
        el = document.createElement('style');
        el.id = styleId;
        document.head.appendChild(el);
    }
    el.textContent = `
:root, :root[data-theme="dark"], :root[data-bs-theme="dark"] {
${paletteToCss(set.dark)}
}
:root[data-theme="light"], :root[data-bs-theme="light"] {
${paletteToCss(set.light)}
}`.trim();
}

export function clearPaletteOverrides() {
    document.getElementById('palette-overrides')?.remove();
}

// Older stored sets may be missing roles added since they were saved (e.g.
// `code`). Fill from the seed palette so the saved theme keeps working
// without dropping the user's customization.
function fillMissingRoles(p: Partial<Palette>, seed: Palette): Palette {
    const filled = { ...p } as Palette;
    for (const r of ROLES) if (!filled[r]) filled[r] = { ...seed[r] };
    return filled;
}

function loadStored(key: string): Stored | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Stored;
        if (!parsed.set?.light || !parsed.set?.dark) return null;
        parsed.set.light = fillMissingRoles(parsed.set.light, SEED_LIGHT_SEPIA);
        parsed.set.dark = fillMissingRoles(parsed.set.dark, SEED_DARK_SEPIA);
        return parsed;
    } catch { return null; }
}

const ctxOf = (p: Preset, mode: Mode, v: Vision): SolverContext => ({
    mode,
    primaryHueAnchor: p.primaryHue,
    secondaryHueAnchor: p.secondaryHue ?? (p.primaryHue + 40) % 360,
    vision: v,
});

export const ThemePicker: React.FC<ThemePickerProps> = ({
    onApplied,
    storageKey = STORAGE_KEY,
    extraPresets = [],
    defaultPresetId,
}) => {
    const allPresets = useMemo(() => {
        const seen = new Set<string>();
        const ordered: Preset[] = [];
        for (const p of [...extraPresets, ...PRESETS]) {
            if (!seen.has(p.id)) { ordered.push(p); seen.add(p.id); }
        }
        return ordered;
    }, [extraPresets]);

    const initial = useMemo(() => {
        const stored = loadStored(storageKey);
        if (stored) {
            const preset = allPresets.find((p) => p.id === stored.presetId) ?? allPresets[0];
            return { preset, set: stored.set, vision: stored.vision ?? 'normal' };
        }
        const startId = defaultPresetId && allPresets.some((p) => p.id === defaultPresetId)
            ? defaultPresetId
            : allPresets[0].id;
        const preset = allPresets.find((p) => p.id === startId) ?? allPresets[0];
        return { preset, set: makeInitialSet(preset), vision: 'normal' as Vision };
    }, [storageKey, allPresets, defaultPresetId]);

    const [presetId, setPresetId] = useState<string>(initial.preset.id);
    const [set, setSet] = useState<PaletteSet>(initial.set);
    const [vision, setVision] = useState<Vision>(initial.vision ?? 'normal');
    const [editMode, setEditMode] = useState<Mode>('dark');
    const [status, setStatus] = useState<{ kind: 'success' | 'info'; msg: string } | null>(null);

    const preset = allPresets.find((p) => p.id === presetId) ?? allPresets[0];

    const ctxFor = useCallback(
        (mode: Mode, v: Vision) => ctxOf(preset, mode, v),
        [preset],
    );

    const onPresetChange = useCallback((id: string) => {
        const p = allPresets.find((q) => q.id === id);
        if (!p) return;
        setPresetId(id);
        const seeded = makeInitialSet(p);
        // The bundled seeds are hand-tuned for trichromat vision, so in normal
        // mode they're a reasonable thing to drop the user on and let them
        // drag from. Under a CVD model they aren't — a seed can't satisfy
        // criteria it was never written against — so solve before showing it.
        // Anchors come from `p`, not the `preset` derived from state, which is
        // still the outgoing one at this point.
        setSet(vision === 'normal' ? seeded : {
            ...seeded,
            light: solveToConvergence(seeded.light, seeded.locks.light, ctxOf(p, 'light', vision)),
            dark: solveToConvergence(seeded.dark, seeded.locks.dark, ctxOf(p, 'dark', vision)),
        });
    }, [allPresets, vision]);

    // Switching the vision model changes the criteria the current palette was
    // solved against, so re-solve both modes in place rather than leaving the
    // user staring at a report full of freshly-failed rows. Locked roles are
    // preserved — the solver moves everything else around them.
    const onVisionChange = useCallback((next: Vision) => {
        setVision(next);
        setSet((s) => ({
            ...s,
            light: solveToConvergence(s.light, s.locks.light, ctxFor('light', next)),
            dark: solveToConvergence(s.dark, s.locks.dark, ctxFor('dark', next)),
        }));
        setStatus({
            kind: 'info',
            msg: next === 'normal'
                ? 'Re-solved for trichromat targets.'
                : `Re-solved for ${VISION_LABELS[next].toLowerCase()}.`,
        });
        setTimeout(() => setStatus(null), 3000);
    }, [ctxFor]);

    const onPaletteChange = (mode: Mode) => (next: Palette) =>
        setSet((s) => ({ ...s, [mode]: next }));
    const onLocksChange = (mode: Mode) => (next: LockMap) =>
        setSet((s) => ({ ...s, locks: { ...s.locks, [mode]: next } }));

    const apply = () => {
        applyPaletteSet(set);
        try {
            localStorage.setItem(storageKey, JSON.stringify({ presetId, set, vision } satisfies Stored));
        } catch { /* ignore quota */ }
        setStatus({ kind: 'success', msg: 'Applied. Saved to this browser.' });
        onApplied?.(set);
        setTimeout(() => setStatus(null), 3000);
    };

    const restoreDefault = () => {
        clearPaletteOverrides();
        try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
        setStatus({ kind: 'info', msg: 'Reverted to bundled theme.' });
        setTimeout(() => setStatus(null), 3000);
    };

    return (
        <Card>
            <Card.Header>Theme Designer</Card.Header>
            <Card.Body>
                <div className="d-flex align-items-center justify-content-between gap-3 mb-2 flex-wrap">
                    <div className="d-flex align-items-center gap-2">
                        <Form.Label htmlFor="palette-preset" className="mb-0 text-muted small">
                            Preset
                        </Form.Label>
                        <Dropdown
                            id="palette-preset"
                            value={presetId}
                            onChange={(e) => onPresetChange(e.target.value)}
                            options={allPresets.map((p) => ({ value: p.id, label: p.name }))}
                            style={{ flex: '0 0 auto' }}
                        />
                        <Form.Label htmlFor="palette-vision" className="mb-0 ms-2 text-muted small">
                            Vision
                        </Form.Label>
                        <Dropdown
                            id="palette-vision"
                            value={vision}
                            onChange={(e) => onVisionChange(e.target.value as Vision)}
                            options={VISIONS.map((v) => ({ value: v, label: VISION_LABELS[v] }))}
                            style={{ flex: '0 0 auto' }}
                        />
                    </div>
                    <Nav
                        variant="pills"
                        activeKey={editMode}
                        onSelect={(k) => k && setEditMode(k as Mode)}
                        className="flex-nowrap"
                    >
                        <Nav.Item>
                            <Nav.Link eventKey="light" className="py-1 px-3 d-flex align-items-center gap-1">
                                <BsSunFill /> Light
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="dark" className="py-1 px-3 d-flex align-items-center gap-1">
                                <BsMoonFill /> Dark
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                </div>
                <p className="text-muted small fst-italic mb-3">
                    {preset.description}
                    {vision !== 'normal' && ` ${VISION_NOTES[vision]} Contrast is measured on the`
                        + ' colours that viewer sees, and colours meaning different things must'
                        + ' stay apart after simulation.'}
                </p>
                <PaletteEditor
                    palette={set[editMode]}
                    locks={set.locks[editMode]}
                    ctx={ctxFor(editMode, vision)}
                    onChange={onPaletteChange(editMode)}
                    onLocksChange={onLocksChange(editMode)}
                />
            </Card.Body>
            <Card.Footer className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                <div className="d-flex gap-2 align-items-center">
                    <Button variant="primary" onClick={apply}>
                        <BsCheck2 className="me-2" /> Apply
                    </Button>
                    <Button variant="outline-secondary" onClick={restoreDefault}>
                        <BsArrowCounterclockwise className="me-2" /> Restore default
                    </Button>
                </div>
                {status && (
                    <Alert variant={status.kind} className="mb-0 py-1 px-2 small">
                        {status.msg}
                    </Alert>
                )}
            </Card.Footer>
        </Card>
    );
};

// Cache key for a solved fallback palette. Includes a content hash so changing
// the preset definition (hue, bg) silently invalidates stale solves. The v2
// prefix forces a re-solve after the role set was extended with secondary +
// per-semantic on-* foregrounds, so old cached solves don't dominate the
// new optimization targets.
function fallbackCacheKey(preset: Preset, vision: Vision): string {
    const sec = preset.secondaryHue ?? (preset.primaryHue + 40) % 360;
    const sig = `${preset.id}|${preset.primaryHue}|${sec}|${preset.bgLight.L},${preset.bgLight.C},${preset.bgLight.h}|${preset.bgDark.L},${preset.bgDark.C},${preset.bgDark.h}|${vision}`;
    let h = 5381;
    for (let i = 0; i < sig.length; i++) h = ((h << 5) + h + sig.charCodeAt(i)) | 0;
    return `palette.bootstrapSolved.v2.${preset.id}.${(h >>> 0).toString(36)}`;
}

function loadCachedSolve(key: string): PaletteSet | null {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PaletteSet;
        if (!parsed?.light || !parsed?.dark) return null;
        // Heal stale caches written before new roles were added so an old
        // cached solve doesn't crash applyPaletteSet on undefined OKLCHs.
        parsed.light = fillMissingRoles(parsed.light, SEED_LIGHT_SEPIA);
        parsed.dark = fillMissingRoles(parsed.dark, SEED_DARK_SEPIA);
        return parsed;
    } catch { return null; }
}

function solveFallback(preset: Preset, vision: Vision): PaletteSet {
    const initial = makeInitialSet(preset);
    const secondaryHueAnchor = preset.secondaryHue ?? (preset.primaryHue + 40) % 360;
    const ctxFor = (mode: Mode) => ({
        mode,
        primaryHueAnchor: preset.primaryHue,
        secondaryHueAnchor,
        vision,
    });
    // Two-phase for a CVD solve: settle the trichromat criteria first, then
    // continue from there. Descending straight from the seed under the extra
    // criteria can park the palette in a much worse local minimum, and the
    // warm start costs nothing next to the solve it replaces.
    const solve = (mode: Mode) => {
        const trichromat = solveToConvergence(
            initial[mode], initial.locks[mode], { ...ctxFor(mode), vision: 'normal' },
        );
        if (vision === 'normal') return trichromat;
        return solveToConvergence(trichromat, initial.locks[mode], ctxFor(mode));
    };
    return { ...initial, light: solve('light'), dark: solve('dark') };
}

// Re-apply a previously-saved override on app boot. If `fallbackPreset` is
// provided and no override is stored, run the gradient-descent solver on that
// preset and apply the result — so the bundled default ships contrast-optimized
// out of the box. Solved fallbacks are cached in localStorage keyed by preset
// content, so the solver only runs on first visit (or after a preset change).
//
// `fallbackVision` lets an app ship a colour-vision-safe default (say a tool
// whose whole job is red/green diffs) without waiting for the user to find
// the picker. It only affects the fallback solve — a stored override already
// carries the vision model it was solved under.
export function bootstrapStoredPalette(
    storageKey = STORAGE_KEY,
    fallbackPreset?: Preset,
    fallbackVision: Vision = 'normal',
) {
    if (typeof document === 'undefined') return;
    const stored = loadStored(storageKey);
    if (stored) { applyPaletteSet(stored.set); return; }
    if (!fallbackPreset) return;

    const cacheKey = fallbackCacheKey(fallbackPreset, fallbackVision);
    let solved = loadCachedSolve(cacheKey);
    if (!solved) {
        solved = solveFallback(fallbackPreset, fallbackVision);
        try { localStorage.setItem(cacheKey, JSON.stringify(solved)); } catch { /* ignore quota */ }
    }
    applyPaletteSet(solved);
}

export default ThemePicker;
